import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Box, Stack, IconButton } from "@mui/material";
import { Refresh } from "@mui/icons-material";
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { useDispatch, useSelector } from "react-redux";
import { updateStateData } from "../../../Store/actions";
import { standardFormatToday } from "../../../api_calls/utils";
import platformConfig from "../../../platformConfig";
import moment from "moment-timezone";
import "moment/locale/en-gb";
import { StyledDateTextField, brandStandardUnderlineInputPropsSx } from "../TextInputs/style";
import { HtmlTooltip } from "../StyledComponents/styled";

const TIMEZONE = platformConfig.timezone;
const PRIMARY_COLOR = "#AF3241";
const PRIMARY_COLOR_DARK = "#8a2833";

/**
 * Converts a date to a moment object in the configured timezone
 */
const toMomentInTimezone = (date) => {
  if (!date) return null;
  return moment.tz(date, TIMEZONE).startOf("day");
};

/**
 * Creates a Date object preserving the selected date regardless of browser timezone
 */
const normalizeDate = (date) => {
  if (!date) return null;
  const m = moment.isMoment(date) ? date : moment(date);
  return new Date(Date.UTC(m.year(), m.month(), m.date(), 0, 0, 0));
};

/**
 * Extracts month index from header text (e.g., "January 2025" -> 0)
 */
const getMonthFromHeader = (headerText) => {
  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];
  for (let i = 0; i < monthNames.length; i++) {
    if (headerText.includes(monthNames[i])) return i;
  }
  return new Date().getMonth();
};

/**
 * Calculates the Monday and Sunday of a given ISO week number and year.
 * Handles year boundary edge cases.
 */
const getWeekBounds = (weekNumber, year, displayedMonth) => {
  let targetYear = year;
  if (displayedMonth === 0 && weekNumber >= 52) targetYear = year - 1;
  if (displayedMonth === 11 && weekNumber === 1) targetYear = year + 1;
  const monday = moment().year(targetYear).isoWeek(weekNumber).startOf("isoWeek");
  const sunday = moment().year(targetYear).isoWeek(weekNumber).endOf("isoWeek");
  return { monday: normalizeDate(monday), sunday: normalizeDate(sunday) };
};

/**
 * Clamps a date to be within min and max bounds
 */
const clampDate = (date, min, max) => {
  if (date < min) return min;
  if (date > max) return max;
  return date;
};

/**
 * Checks if a week has any overlap with the global date range
 */
const isWeekSelectableInRange = (weekNumber, year, displayedMonth, globalMin, globalMax) => {
  const { monday, sunday } = getWeekBounds(weekNumber, year, displayedMonth);
  return monday <= globalMax && sunday >= globalMin;
};

/**
 * Generates slot props for MUI DatePicker with custom styling.
 * Always reserves space for the helper text to prevent layout shifts.
 */
const getSlotProps = (onOpenPicker, isPickerOpen, onBlurHandler, inputRef, showSelection, onKeyDownHandler, fieldError) => {
  const hasError = fieldError !== "" && fieldError != null;

  return {
    textField: {
      variant: "standard",
      fullWidth: true,
      autoComplete: "off",
      onClick: onOpenPicker,
      onBlur: onBlurHandler,
      onKeyDown: onKeyDownHandler,
      inputRef: inputRef,
      error: hasError,
      helperText: hasError ? fieldError : " ",
      FormHelperTextProps: {
        sx: {
          marginLeft: 0,
          fontSize: "0.75rem",
          color: hasError ? "#d32f2f" : "transparent",
          userSelect: hasError ? "auto" : "none",
        },
      },
      InputProps: {
        readOnly: !isPickerOpen,
        sx: {
          ...brandStandardUnderlineInputPropsSx,
          cursor: isPickerOpen ? "text" : "pointer",
          caretColor: showSelection ? PRIMARY_COLOR : "transparent",
          "& input": {
            cursor: isPickerOpen ? "text" : "pointer",
            caretColor: showSelection ? PRIMARY_COLOR : "transparent",
            "&::selection": {
              backgroundColor: showSelection ? "rgba(175, 50, 65, 0.3)" : "transparent",
            },
          },
        },
      },
      InputLabelProps: {
        sx: { "&.Mui-focused": { color: PRIMARY_COLOR } },
      },
    },
    day: {
      sx: {
        width: 28, height: 28, fontSize: "0.75rem",
        "&:focus:not(.Mui-selected)": {
          backgroundColor: "transparent", outline: "none",
          "&:hover": { backgroundColor: "rgba(138, 40, 51, 0.2)" },
        },
        "&.Mui-selected": {
          backgroundColor: PRIMARY_COLOR, color: "#fff",
          "&:hover": { backgroundColor: PRIMARY_COLOR_DARK },
          "&:focus": { backgroundColor: PRIMARY_COLOR },
          "&.Mui-focusVisible": {
            backgroundColor: PRIMARY_COLOR,
            outline: `2px solid ${PRIMARY_COLOR_DARK}`, outlineOffset: -2,
          },
        },
        "&:hover": { backgroundColor: "rgba(138, 40, 51, 0.2)" },
        "&.Mui-focusVisible:not(.Mui-selected)": {
          outline: `2px solid ${PRIMARY_COLOR}`, outlineOffset: -2,
          backgroundColor: "rgba(175, 50, 65, 0.08)",
        },
        "&.MuiPickersDay-today": {
          border: "none",
          "&:not(.Mui-selected)": { border: "none" },
        },
        "&.MuiPickersDay-dayOutsideMonth:not(.Mui-disabled)": { color: "#777" },
        "&.Mui-disabled:not(.MuiPickersDay-dayOutsideMonth)": {
          color: "#ccc", opacity: 1,
          "&:hover": { cursor: "not-allowed" },
        },
        "&.Mui-disabled.MuiPickersDay-dayOutsideMonth": {
          color: "#e0e0e0", opacity: 0.5, cursor: "not-allowed",
        },
      },
    },
    calendarHeader: {
      sx: {
        paddingLeft: 1, paddingRight: 1, marginTop: 0, marginBottom: 0, minHeight: 36,
        "& .MuiPickersCalendarHeader-label": { fontSize: "0.875rem" },
        "& .MuiPickersCalendarHeader-switchViewButton": {
          color: PRIMARY_COLOR, padding: 0.5,
          "&:focus": { outline: `2px solid ${PRIMARY_COLOR}`, outlineOffset: -2 },
        },
        "& .MuiPickersArrowSwitcher-button": {
          color: PRIMARY_COLOR, padding: 0.5,
          "&:focus": { outline: `2px solid ${PRIMARY_COLOR}`, outlineOffset: -2 },
        },
      },
    },
    yearButton: {
      sx: {
        "&.Mui-selected": {
          backgroundColor: PRIMARY_COLOR, color: "#fff",
          "&:hover": { backgroundColor: PRIMARY_COLOR_DARK },
          "&:focus": { backgroundColor: PRIMARY_COLOR },
          "&.Mui-focusVisible": {
            backgroundColor: PRIMARY_COLOR,
            outline: `2px solid ${PRIMARY_COLOR_DARK}`, outlineOffset: -2,
          },
        },
        "&.Mui-focusVisible:not(.Mui-selected)": {
          outline: `2px solid ${PRIMARY_COLOR}`, outlineOffset: -2,
          backgroundColor: "rgba(175, 50, 65, 0.08)",
        },
      },
    },
    monthButton: {
      sx: {
        "&.Mui-selected": {
          backgroundColor: PRIMARY_COLOR, color: "#fff",
          "&:hover": { backgroundColor: PRIMARY_COLOR_DARK },
          "&:focus": { backgroundColor: PRIMARY_COLOR },
          "&.Mui-focusVisible": {
            backgroundColor: PRIMARY_COLOR,
            outline: `2px solid ${PRIMARY_COLOR_DARK}`, outlineOffset: -2,
          },
        },
        "&.Mui-focusVisible:not(.Mui-selected)": {
          outline: `2px solid ${PRIMARY_COLOR}`, outlineOffset: -2,
          backgroundColor: "rgba(175, 50, 65, 0.08)",
        },
      },
    },
    layout: {
      sx: {
        "& .MuiDateCalendar-root": { width: 260, height: "auto" },
        "& .MuiDayCalendar-header .MuiDayCalendar-weekDayLabel": {
          width: 28, height: 28, fontSize: "0.7rem", color: "#555",
        },
        "& .MuiDayCalendar-weekNumberLabel": {
          width: 28, fontSize: "0.7rem", color: "#555", fontWeight: 500, backgroundColor: "#f5f5f5",
        },
        "& .MuiDayCalendar-weekNumber": {
          width: 28, height: 28, fontSize: "0.7rem", cursor: "pointer",
          backgroundColor: "#f5f5f5", color: "#555",
          transition: "color 0.15s ease, background-color 0.15s ease",
          "&:hover": { color: PRIMARY_COLOR_DARK, fontWeight: 600, backgroundColor: "#e0e0e0" },
        },
        "& .MuiDayCalendar-weekNumber.week-number-disabled": {
          color: "#ccc", cursor: "not-allowed",
          "&:hover": { color: "#ccc", fontWeight: "normal" },
        },
        "& .MuiDayCalendar-slideTransition": { minHeight: 182, overflow: "hidden" },
        "& .MuiPickersDay-root": { width: 28, height: 28, color: "#000" },
        "& .MuiYearCalendar-root": {
          width: 260, maxHeight: 220, padding: "4px 0",
          "& .MuiPickersYear-yearButton": {
            width: 56, height: 28, fontSize: "0.8rem", margin: "2px 4px",
            "&.Mui-selected": {
              backgroundColor: PRIMARY_COLOR, color: "#fff",
              "&:hover": { backgroundColor: PRIMARY_COLOR_DARK },
            },
            "&.Mui-focusVisible:not(.Mui-selected)": {
              outline: `2px solid ${PRIMARY_COLOR}`, outlineOffset: -2,
              backgroundColor: "rgba(175, 50, 65, 0.08)",
            },
            "&.Mui-selected.Mui-focusVisible": {
              backgroundColor: PRIMARY_COLOR,
              outline: `2px solid ${PRIMARY_COLOR_DARK}`, outlineOffset: -2,
            },
          },
        },
        "& .MuiMonthCalendar-root": {
          width: 260, padding: "4px 0",
          "& .MuiPickersMonth-monthButton": {
            width: 56, height: 28, fontSize: "0.8rem", margin: "2px 4px",
            "&.Mui-selected": {
              backgroundColor: PRIMARY_COLOR, color: "#fff",
              "&:hover": { backgroundColor: PRIMARY_COLOR_DARK },
            },
            "&.Mui-disabled": { opacity: 0.4, cursor: "not-allowed" },
            "&.Mui-focusVisible:not(.Mui-selected)": {
              outline: `2px solid ${PRIMARY_COLOR}`, outlineOffset: -2,
              backgroundColor: "rgba(175, 50, 65, 0.08)",
            },
            "&.Mui-selected.Mui-focusVisible": {
              backgroundColor: PRIMARY_COLOR,
              outline: `2px solid ${PRIMARY_COLOR_DARK}`, outlineOffset: -2,
            },
          },
        },
      },
    },
    popper: {
      placement: "bottom-start",
      disableAutoFocus: true,
      modifiers: [{ name: "preventOverflow", options: { padding: 8 } }],
      sx: {
        zIndex: 1300,
        "& .MuiPaper-root": { boxShadow: "0 4px 20px rgba(0,0,0,0.15)" },
      },
    },
  };
};

const DateRangeInputFields = ({ reduxKeywordToSaveValue, minSelectableDate, maxSelectableDate }) => {

  const currentStateData = useSelector((state) => state.userSelections);
  const dispatchAction = useDispatch();
  const containerRef = useRef(null);
  const startInputRef = useRef(null);
  const endInputRef = useRef(null);
  const isClosingRef = useRef(false);

  /**
   * Refs that always hold the latest selected moment values.
   * Required because MUI fires onClose synchronously after onChange on day click,
   * before React has flushed the corresponding state update. Reading from refs in
   * validate/commit callbacks avoids stale-closure bugs in that scenario.
   */
  const latestStartDateRef = useRef(null);
  const latestEndDateRef = useRef(null);

  const { minDate, maxDate } = useMemo(() => {
    const today = standardFormatToday();
    const yesterday = normalizeDate(moment(today).subtract(1, "day"));
    const min = minSelectableDate && minSelectableDate !== ""
      ? normalizeDate(toMomentInTimezone(minSelectableDate))
      : normalizeDate(toMomentInTimezone("2022-10-01"));
    const max = maxSelectableDate && maxSelectableDate !== ""
      ? normalizeDate(toMomentInTimezone(maxSelectableDate))
      : yesterday;
    return { minDate: min, maxDate: max };
  }, [minSelectableDate, maxSelectableDate]);

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [startPickerOpen, setStartPickerOpen] = useState(false);
  const [endPickerOpen, setEndPickerOpen] = useState(false);
  const [startError, setStartError] = useState("");
  const [endError, setEndError] = useState("");
  const [startPickerView, setStartPickerView] = React.useState("day");
  const [endPickerView, setEndPickerView] = React.useState("day");
  const [showStartSelection, setShowStartSelection] = useState(false);
  const [showEndSelection, setShowEndSelection] = useState(false);

  const isAtDefault = useMemo(() => {
    if (!startDate || !endDate || !minDate || !maxDate) return true;
    const startNorm = normalizeDate(startDate);
    const endNorm = normalizeDate(endDate);
    return startNorm?.getTime() === minDate?.getTime() && endNorm?.getTime() === maxDate?.getTime();
  }, [startDate, endDate, minDate, maxDate]);

  /**
   * Commits date range to Redux store
   */
  const commitToRedux = useCallback((newStart, newEnd) => {
    dispatchAction(updateStateData({
      [reduxKeywordToSaveValue]: {
        ...currentStateData[reduxKeywordToSaveValue],
        from: normalizeDate(newStart),
        to: normalizeDate(newEnd),
      },
    }));
  }, [dispatchAction, reduxKeywordToSaveValue, currentStateData]);

  const preventScrollOnViewChange = useCallback(() => {
    const currentScrollPos = window.scrollY;
    requestAnimationFrame(() => window.scrollTo(0, currentScrollPos));
  }, []);

  const isStartDateValid = useCallback((date) => {
    if (!date || !moment.isMoment(date) || !date.isValid()) return false;
    const minMoment = moment(minDate);
    const maxMoment = latestEndDateRef.current || moment(maxDate);
    return !date.isBefore(minMoment, "day") && !date.isAfter(maxMoment, "day");
  }, [minDate, maxDate]);

  const isEndDateValid = useCallback((date) => {
    if (!date || !moment.isMoment(date) || !date.isValid()) return false;
    const minMoment = latestStartDateRef.current || moment(minDate);
    const maxMoment = moment(maxDate);
    return !date.isBefore(minMoment, "day") && !date.isAfter(maxMoment, "day");
  }, [minDate, maxDate]);

  const getStartValidationMessage = useCallback((date) => {
    if (!date || !moment.isMoment(date) || !date.isValid()) {
      return "Please enter a valid date (DD/MM/YYYY)";
    }
    const minMoment = moment(minDate);
    const maxMoment = latestEndDateRef.current || moment(maxDate);
    if (date.isBefore(minMoment, "day")) {
      return minMoment.isSame(moment(), "day")
        ? "Start date cannot be in the past"
        : `Start date cannot be before ${minMoment.format("DD/MM/YYYY")}`;
    }
    if (date.isAfter(maxMoment, "day")) return "Start date cannot be after the end date";
    return "";
  }, [minDate, maxDate]);

  const getEndValidationMessage = useCallback((date) => {
    if (!date || !moment.isMoment(date) || !date.isValid()) {
      return "Please enter a valid date (DD/MM/YYYY)";
    }
    const minMoment = latestStartDateRef.current || moment(minDate);
    const maxMoment = moment(maxDate);
    if (date.isBefore(minMoment, "day")) return "End date cannot be before the start date";
    if (date.isAfter(maxMoment, "day")) {
      return maxMoment.isSame(moment(), "day")
        ? "End date cannot be in the future"
        : `End date cannot be after ${maxMoment.format("DD/MM/YYYY")}`;
    }
    return "";
  }, [minDate, maxDate]);

  /**
   * Validates start date and commits to Redux. Reads from latestStartDateRef to
   * avoid stale state when called synchronously from onClose after a day click.
   * On invalid input, reverts to minDate and shows a descriptive error message.
   */
  const validateAndCommitStart = useCallback(() => {
    const currentStart = latestStartDateRef.current;
    const currentEnd = latestEndDateRef.current;
    if (isStartDateValid(currentStart)) {
      setStartError("");
      commitToRedux(currentStart, currentEnd);
    } else {
      setStartError(getStartValidationMessage(currentStart));
      const fallback = moment(minDate);
      latestStartDateRef.current = fallback;
      setStartDate(fallback);
      commitToRedux(minDate, currentEnd);
    }
  }, [isStartDateValid, getStartValidationMessage, minDate, commitToRedux]);

  /**
   * Validates end date and commits to Redux. Reads from latestEndDateRef to
   * avoid stale state when called synchronously from onClose after a day click.
   * On invalid input, reverts to maxDate and shows a descriptive error message.
   */
  const validateAndCommitEnd = useCallback(() => {
    const currentStart = latestStartDateRef.current;
    const currentEnd = latestEndDateRef.current;
    if (isEndDateValid(currentEnd)) {
      setEndError("");
      commitToRedux(currentStart, currentEnd);
    } else {
      setEndError(getEndValidationMessage(currentEnd));
      const fallback = moment(maxDate);
      latestEndDateRef.current = fallback;
      setEndDate(fallback);
      commitToRedux(currentStart, maxDate);
    }
  }, [isEndDateValid, getEndValidationMessage, maxDate, commitToRedux]);

  /**
   * Closes the start picker, validates input, and ensures the input is
   * fully blurred after MUI's async focus restoration completes.
   * Uses dual-timeout approach for reliable blur in edge cases.
   */
  const closeStartPicker = useCallback(() => {
    isClosingRef.current = true;
    validateAndCommitStart();
    setStartPickerOpen(false);
    setStartPickerView("day");
    window.getSelection()?.removeAllRanges();
    // Primary blur attempt after MUI's typical focus restoration
    setTimeout(() => {
      startInputRef.current?.blur();
    }, 100);
    // Secondary fallback for edge cases where validation triggers delayed focus
    setTimeout(() => {
      startInputRef.current?.blur();
      isClosingRef.current = false;
    }, 600);
  }, [validateAndCommitStart]);

  /**
   * Closes the end picker, validates input, and ensures the input is
   * fully blurred after MUI's async focus restoration completes.
   * Uses dual-timeout approach for reliable blur in edge cases.
   */
  const closeEndPicker = useCallback(() => {
    isClosingRef.current = true;
    validateAndCommitEnd();
    setEndPickerOpen(false);
    setEndPickerView("day");
    window.getSelection()?.removeAllRanges();
    // Primary blur attempt after MUI's typical focus restoration
    setTimeout(() => {
      endInputRef.current?.blur();
    }, 100);
    // Secondary fallback for edge cases where validation triggers delayed focus
    setTimeout(() => {
      endInputRef.current?.blur();
      isClosingRef.current = false;
    }, 300);
  }, [validateAndCommitEnd]);

  /**
   * Handles start input blur. Skipped when closeStartPicker already validated.
   */
  const handleStartBlur = useCallback(() => {
    if (isClosingRef.current) return;
    validateAndCommitStart();
  }, [validateAndCommitStart]);

  /**
   * Handles end input blur. Skipped when closeEndPicker already validated.
   */
  const handleEndBlur = useCallback(() => {
    if (isClosingRef.current) return;
    validateAndCommitEnd();
  }, [validateAndCommitEnd]);

  useEffect(() => {
    if (minDate && maxDate) {
      const startMoment = moment(minDate);
      const endMoment = moment(maxDate);
      latestStartDateRef.current = startMoment;
      latestEndDateRef.current = endMoment;
      setStartDate(startMoment);
      setEndDate(endMoment);
      setStartError("");
      setEndError("");
      commitToRedux(minDate, maxDate);
    }
  }, [minDate?.getTime(), maxDate?.getTime()]);

  useEffect(() => { setShowStartSelection(startPickerOpen); }, [startPickerOpen]);
  useEffect(() => { setShowEndSelection(endPickerOpen); }, [endPickerOpen]);

  /**
   * One-shot focus reclaim after MUI's popper steals focus on open.
   * Guarded by isClosingRef so it never fires during close.
   */
  useEffect(() => {
    if (!startPickerOpen && !endPickerOpen) return;
    const activeRef = startPickerOpen ? startInputRef : endInputRef;
    const timerId = setTimeout(() => {
      if (isClosingRef.current) return;
      activeRef.current?.focus();
    }, 50);
    return () => clearTimeout(timerId);
  }, [startPickerOpen, endPickerOpen]);

  const handleStartKeyDown = useCallback((e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
      if (startPickerOpen) {
        closeStartPicker();
      } else {
        validateAndCommitStart();
      }
    } else if (e.key === "Tab" && startPickerOpen) {
      closeStartPicker();
    }
  }, [startPickerOpen, closeStartPicker, validateAndCommitStart]);

  const handleEndKeyDown = useCallback((e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
      if (endPickerOpen) {
        closeEndPicker();
      } else {
        validateAndCommitEnd();
      }
    } else if (e.key === "Tab" && endPickerOpen) {
      closeEndPicker();
    }
  }, [endPickerOpen, closeEndPicker, validateAndCommitEnd]);
  /**
   * Handles start date changes from picker (day click, year/month navigation).
   * Updates the ref synchronously so validate/commit callbacks always see the
   * latest value, even when called within the same event cycle (e.g. onClose
   * firing immediately after onChange on a day click).
   * Redux commit happens on blur/close/enter.
   */
  const handleStartDateChange = useCallback((newValue) => {
    if (!newValue || !newValue.isValid()) return;
    latestStartDateRef.current = newValue;
    setStartDate(newValue);
    if (isStartDateValid(newValue)) setStartError("");
  }, [isStartDateValid]);

  /**
   * Handles end date changes from picker (day click, year/month navigation).
   * Updates the ref synchronously so validate/commit callbacks always see the
   * latest value, even when called within the same event cycle (e.g. onClose
   * firing immediately after onChange on a day click).
   * Redux commit happens on blur/close/enter.
   */
  const handleEndDateChange = useCallback((newValue) => {
    if (!newValue || !newValue.isValid()) return;
    latestEndDateRef.current = newValue;
    setEndDate(newValue);
    if (isEndDateValid(newValue)) setEndError("");
  }, [isEndDateValid]);

  const handleReset = useCallback(() => {
    if (minDate && maxDate) {
      const startMoment = moment(minDate);
      const endMoment = moment(maxDate);
      latestStartDateRef.current = startMoment;
      latestEndDateRef.current = endMoment;
      setStartDate(startMoment);
      setEndDate(endMoment);
      setStartError("");
      setEndError("");
      commitToRedux(minDate, maxDate);
    }
  }, [minDate, maxDate, commitToRedux]);

  const handleWeekSelect = useCallback((weekStart, weekEnd) => {
    const clampedStart = clampDate(weekStart, minDate, maxDate);
    const clampedEnd = clampDate(weekEnd, minDate, maxDate);
    if (clampedStart <= clampedEnd) {
      const startMoment = moment(clampedStart);
      const endMoment = moment(clampedEnd);
      latestStartDateRef.current = startMoment;
      latestEndDateRef.current = endMoment;
      setStartDate(startMoment);
      setEndDate(endMoment);
      setStartError("");
      setEndError("");
      commitToRedux(clampedStart, clampedEnd);

      // Close pickers and blur inputs using same pattern as close functions
      isClosingRef.current = true;
      const wasStartOpen = startPickerOpen;
      const wasEndOpen = endPickerOpen;
      setStartPickerOpen(false);
      setEndPickerOpen(false);
      window.getSelection()?.removeAllRanges();

      // Dual-timeout blur for reliable focus removal
      setTimeout(() => {
        if (wasStartOpen) startInputRef.current?.blur();
        if (wasEndOpen) endInputRef.current?.blur();
      }, 100);
      setTimeout(() => {
        if (wasStartOpen) startInputRef.current?.blur();
        if (wasEndOpen) endInputRef.current?.blur();
        isClosingRef.current = false;
      }, 300);
    }
  }, [minDate, maxDate, commitToRedux, startPickerOpen, endPickerOpen]);

  const updateWeekNumberStyling = useCallback(() => {
    if (!minDate || !maxDate) return;
    const weekNumbers = document.querySelectorAll(".MuiDayCalendar-weekNumber");
    weekNumbers.forEach((weekEl) => {
      const weekText = weekEl.textContent;
      if (!weekText) return;
      const weekNumber = parseInt(weekText, 10);
      if (isNaN(weekNumber)) return;
      const popper = weekEl.closest(".MuiPickersPopper-root");
      if (!popper) return;
      const monthHeader = popper.querySelector(".MuiPickersCalendarHeader-label");
      if (!monthHeader) return;
      const headerText = monthHeader.textContent || "";
      const yearMatch = headerText.match(/\d{4}/);
      const displayedYear = yearMatch ? parseInt(yearMatch[0], 10) : new Date().getFullYear();
      const displayedMonth = getMonthFromHeader(headerText);
      const isSelectable = isWeekSelectableInRange(weekNumber, displayedYear, displayedMonth, minDate, maxDate);
      if (isSelectable) {
        weekEl.classList.remove("week-number-disabled");
      } else {
        weekEl.classList.add("week-number-disabled");
      }
    });
  }, [minDate, maxDate]);

  useEffect(() => {
    const handleWeekClick = (e) => {
      const target = e.target;
      if (!target || !target.classList.contains("MuiDayCalendar-weekNumber")) return;
      e.preventDefault();
      e.stopPropagation();
      if (target.classList.contains("week-number-disabled")) return;
      const weekText = target.textContent;
      if (!weekText) return;
      const weekNumber = parseInt(weekText, 10);
      if (isNaN(weekNumber)) return;
      const popper = target.closest(".MuiPickersPopper-root");
      if (!popper) return;
      const monthHeader = popper.querySelector(".MuiPickersCalendarHeader-label");
      if (!monthHeader) return;
      const headerText = monthHeader.textContent || "";
      const yearMatch = headerText.match(/\d{4}/);
      const displayedYear = yearMatch ? parseInt(yearMatch[0], 10) : new Date().getFullYear();
      const displayedMonth = getMonthFromHeader(headerText);
      const { monday, sunday } = getWeekBounds(weekNumber, displayedYear, displayedMonth);
      handleWeekSelect(monday, sunday);
    };
    document.addEventListener("mousedown", handleWeekClick, true);
    return () => document.removeEventListener("mousedown", handleWeekClick, true);
  }, [handleWeekSelect]);

  useEffect(() => {
    if (!startPickerOpen && !endPickerOpen) return;
    const timeoutId = setTimeout(updateWeekNumberStyling, 50);
    const observer = new MutationObserver(() => updateWeekNumberStyling());
    observer.observe(document.body, { childList: true, subtree: true });
    return () => {
      clearTimeout(timeoutId);
      observer.disconnect();
    };
  }, [updateWeekNumberStyling, startPickerOpen, endPickerOpen]);

  const startSlotProps = getSlotProps(
    () => { setStartPickerOpen(true); setStartError(""); },
    startPickerOpen, handleStartBlur, startInputRef, showStartSelection, handleStartKeyDown, startError
  );
  const endSlotProps = getSlotProps(
    () => { setEndPickerOpen(true); setEndError(""); },
    endPickerOpen, handleEndBlur, endInputRef, showEndSelection, handleEndKeyDown, endError
  );

  return (
    <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale="en-gb">
      <Stack ref={containerRef} direction="row" spacing={1} alignItems="flex-start" sx={{ mt: 0.3 }}>
        <Box sx={{ width: 100, pr: 1 }}>
          <DesktopDatePicker
            label="Start Date"
            value={startDate}
            onChange={handleStartDateChange}
            open={startPickerOpen}
            onOpen={() => setStartPickerOpen(true)}
            onClose={closeStartPicker}
            view={startPickerView}
            onViewChange={(newView) => { setStartPickerView(newView); preventScrollOnViewChange(); }}
            format="DD/MM/YYYY"
            minDate={moment(minDate)}
            maxDate={latestEndDateRef.current || moment(maxDate)}
            displayWeekNumber
            showDaysOutsideCurrentMonth
            views={["year", "month", "day"]}
            openTo="day"
            slots={{ textField: StyledDateTextField }}
            slotProps={startSlotProps}
          />
        </Box>
        <Box sx={{ width: 100 }}>
          <DesktopDatePicker
            label="End Date"
            value={endDate}
            onChange={handleEndDateChange}
            open={endPickerOpen}
            onOpen={() => setEndPickerOpen(true)}
            onClose={closeEndPicker}
            view={endPickerView}
            onViewChange={(newView) => { setEndPickerView(newView); preventScrollOnViewChange(); }}
            format="DD/MM/YYYY"
            minDate={latestStartDateRef.current || moment(minDate)}
            maxDate={moment(maxDate)}
            displayWeekNumber
            showDaysOutsideCurrentMonth
            views={["year", "month", "day"]}
            openTo="day"
            slots={{ textField: StyledDateTextField }}
            slotProps={endSlotProps}
          />
        </Box>
        {/* Reset button - only visible when date range differs from default */}
        <HtmlTooltip title={isAtDefault ? "" : "Reset to full date range"}>
          <span>
            <IconButton
              onClick={handleReset}
              disabled={isAtDefault}
              size="small"
              sx={{
                color: isAtDefault ? "#ccc" : PRIMARY_COLOR,
                "&:hover": {
                  backgroundColor: isAtDefault ? "transparent" : "rgba(175, 50, 65, 0.08)",
                },
                "&.Mui-disabled": { color: "#ccc" },
                pt: 1.9,
              }}
            >
              <Refresh fontSize="small" />
            </IconButton>
          </span>
        </HtmlTooltip>
      </Stack>
    </LocalizationProvider>
  );
};

export default DateRangeInputFields;
